import { StateCreator } from 'zustand';
import { AppState } from '../types';

export interface PremiumSliceType {
    unlockedPremiumTools: string[];
    isPremiumModalOpen: boolean;
    /** Optional Deep-Link auf Tool-Detail (z. B. Tarifübersicht) */
    premiumModalFocusToolId: string | null;
    unlockTool: (toolId: string) => void;
    cancelSubscription: (toolId: string) => void;
    /**
     * Gottmodus / Coach-Demo: Premium-Tool sofort freischalten oder sperren,
     * ohne 12-Monats-Kündigungsfrist und ohne Stripe/Kostenbuchung.
     */
    adminForceSetPremiumTool: (toolId: string, enabled: boolean) => void;
    hydrateUnlockedPremiumTools: (tools: string[] | undefined | null) => void;
    openPremiumModal: (toolId?: string) => void;
    closePremiumModal: () => void;
}

const normalizeTools = (tools: unknown): string[] =>
    Array.isArray(tools) ? tools.filter((id): id is string => typeof id === 'string' && id.length > 0) : [];

export const createPremiumSlice: StateCreator<AppState, [], [], PremiumSliceType> = (set, get) => ({
    unlockedPremiumTools: [],
    isPremiumModalOpen: false,
    premiumModalFocusToolId: null,

    hydrateUnlockedPremiumTools: (tools) => {
        set({ unlockedPremiumTools: normalizeTools(tools) });
    },

    unlockTool: (toolId: string) => {
        const state = get();
        const alreadyUnlocked = state.unlockedPremiumTools.includes(toolId);
        const next = alreadyUnlocked
            ? state.unlockedPremiumTools
            : [...state.unlockedPremiumTools, toolId];

        set({
            unlockedPremiumTools: next,
            clientData: state.clientData
                ? { ...state.clientData, unlockedPremiumTools: next }
                : state.clientData,
            isDirty: true,
        });

        // Sofort in die Akte schreiben (auch ohne Bezahlroutine) — überlebt Reload
        void (async () => {
            try {
                const { auth } = await import('../../config/firebase');
                const { patchKalkulationMandantRoot } = await import('../../utils/premiumCostBridge');
                const useAdminStore = (await import('../adminStore')).default;
                const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
                if (!uid) return;
                await patchKalkulationMandantRoot(uid, {
                    unlockedPremiumTools: next,
                    'clientData.unlockedPremiumTools': next,
                });
            } catch (err) {
                console.error('Premium-Unlock Persistenz fehlgeschlagen:', err);
            }
        })();

        // Erste Freischaltung: BWA + Change-Radar (skip wenn bereits gebucht, z. B. PERent-Tarif)
        if (!alreadyUnlocked) {
            void (async () => {
                try {
                    const activatedMap =
                        get().clientData?.premiumToolActivatedAt &&
                        typeof get().clientData.premiumToolActivatedAt === 'object'
                            ? (get().clientData.premiumToolActivatedAt as Record<string, string>)
                            : {};
                    if (activatedMap[toolId]) return;

                    const { PREMIUM_TOOL_BOOKING_CATALOG, registerPremiumToolBooking } = await import(
                        '../../utils/premiumCostBridge'
                    );
                    const catalog = PREMIUM_TOOL_BOOKING_CATALOG[toolId];
                    if (!catalog) return;
                    await registerPremiumToolBooking({
                        toolId,
                        toolName: catalog.toolName,
                        price: catalog.price,
                        billingMode: catalog.billingMode,
                    });
                } catch (err) {
                    console.error('Premium-Tool Kostenbuchung fehlgeschlagen:', err);
                }
            })();
        }

        // PERent lokaler Lizenzstatus syncen
        if (toolId === 'pe-rent') {
            void (async () => {
                try {
                    const { usePERentStore } = await import('../usePERentStore');
                    if (!usePERentStore.getState().isLicensed) {
                        usePERentStore.setState({ isLicensed: true, licenseType: 'sub_24' });
                    }
                } catch {
                    /* ignore */
                }
            })();
        }
    },

    cancelSubscription: (toolId: string) => {
        void (async () => {
            try {
                const {
                    isPremiumCancelAllowed,
                    registerPremiumToolCancellation,
                } = await import('../../utils/premiumCostBridge');

                const state = get();
                const activatedMap =
                    state.clientData?.premiumToolActivatedAt &&
                    typeof state.clientData.premiumToolActivatedAt === 'object'
                        ? (state.clientData.premiumToolActivatedAt as Record<string, string>)
                        : {};
                const activatedAt = activatedMap[toolId] || null;

                if (!isPremiumCancelAllowed(activatedAt)) {
                    console.warn('Premium-Cancel abgelehnt: Mindestlaufzeit 12 Monate nicht erreicht');
                    return;
                }

                const next = get().unlockedPremiumTools.filter((id) => id !== toolId);
                const nextActivated = { ...activatedMap };
                delete nextActivated[toolId];

                set({
                    unlockedPremiumTools: next,
                    clientData: get().clientData
                        ? {
                              ...get().clientData,
                              unlockedPremiumTools: next,
                              premiumToolActivatedAt: nextActivated,
                          }
                        : get().clientData,
                    isDirty: true,
                });

                const { auth } = await import('../../config/firebase');
                const { patchKalkulationMandantRoot } = await import('../../utils/premiumCostBridge');
                const useAdminStore = (await import('../adminStore')).default;
                const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
                if (uid) {
                    await patchKalkulationMandantRoot(uid, {
                        unlockedPremiumTools: next,
                        'clientData.unlockedPremiumTools': next,
                        'clientData.premiumToolActivatedAt': nextActivated,
                    });
                }

                await registerPremiumToolCancellation(toolId);

                if (toolId === 'pe-rent') {
                    try {
                        const { usePERentStore } = await import('../usePERentStore');
                        usePERentStore.setState({ isLicensed: false, licenseType: null });
                    } catch {
                        /* ignore */
                    }
                }
            } catch (err) {
                console.error('Premium-Cancel Persistenz fehlgeschlagen:', err);
            }
        })();
    },

    adminForceSetPremiumTool: (toolId: string, enabled: boolean) => {
        void (async () => {
            try {
                const state = get();
                const id = String(toolId || '').trim();
                if (!id) return;

                const activatedMap =
                    state.clientData?.premiumToolActivatedAt &&
                    typeof state.clientData.premiumToolActivatedAt === 'object'
                        ? { ...(state.clientData.premiumToolActivatedAt as Record<string, string>) }
                        : {};
                const planMap =
                    state.clientData?.premiumToolPlanType &&
                    typeof state.clientData.premiumToolPlanType === 'object'
                        ? { ...(state.clientData.premiumToolPlanType as Record<string, string>) }
                        : {};

                let next = normalizeTools(state.unlockedPremiumTools);
                if (enabled) {
                    if (!next.includes(id)) next = [...next, id];
                    if (!activatedMap[id]) activatedMap[id] = new Date().toISOString();
                } else {
                    next = next.filter((t) => t !== id);
                    delete activatedMap[id];
                    delete planMap[id];
                }

                set({
                    unlockedPremiumTools: next,
                    clientData: state.clientData
                        ? {
                              ...state.clientData,
                              unlockedPremiumTools: next,
                              premiumToolActivatedAt: activatedMap,
                              premiumToolPlanType: planMap,
                          }
                        : state.clientData,
                    isDirty: true,
                });

                const { auth } = await import('../../config/firebase');
                const { patchKalkulationMandantRoot } = await import('../../utils/premiumCostBridge');
                const useAdminStore = (await import('../adminStore')).default;
                const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
                if (uid) {
                    await patchKalkulationMandantRoot(uid, {
                        unlockedPremiumTools: next,
                        'clientData.unlockedPremiumTools': next,
                        'clientData.premiumToolActivatedAt': activatedMap,
                        'clientData.premiumToolPlanType': planMap,
                    });
                }

                if (id === 'pe-rent') {
                    try {
                        const { usePERentStore } = await import('../usePERentStore');
                        usePERentStore.setState(
                            enabled
                                ? { isLicensed: true, licenseType: 'sub_24' }
                                : { isLicensed: false, licenseType: null }
                        );
                    } catch {
                        /* ignore */
                    }
                }
            } catch (err) {
                console.error('Admin Premium-Toggle fehlgeschlagen:', err);
            }
        })();
    },

    openPremiumModal: (toolId) =>
        set({
            isPremiumModalOpen: true,
            premiumModalFocusToolId: typeof toolId === 'string' && toolId.length > 0 ? toolId : null,
        }),

    closePremiumModal: () =>
        set({
            isPremiumModalOpen: false,
            premiumModalFocusToolId: null,
        }),
});
